import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output, input } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import * as _ from 'lodash-es';
import { distinctUntilChanged } from 'rxjs/operators';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatAutocompleteTrigger, MatAutocomplete, MatOption, MatOptgroup } from '@angular/material/autocomplete';
import { RxReactiveFormsModule } from '@rxweb/reactive-form-validators';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatPaginator } from '@angular/material/paginator';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'app-autocomplete-filter',
    templateUrl: './autocomplete-filter.component.html',
    styleUrls: ['./autocomplete-filter.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatFormField, MatLabel, MatInput, FormsModule, MatAutocompleteTrigger, ReactiveFormsModule, RxReactiveFormsModule, MatAutocomplete, MatOption, MatProgressSpinner, MatPaginator, MatOptgroup, MatError, TranslatePipe]
})
export class AutocompleteFilterComponent implements OnInit, OnDestroy {

    readonly label = input<string>();
    readonly error = input<string>();
    readonly placeholder = input<string>();
    readonly required = input<boolean>();
    @Input() set disabled(value: boolean | undefined) {
        if (value !== undefined) {
            if (value) {
                this.control?.disable();
            } else {
                this.control?.enable();
            }
        }
    };
    get disabled() {
        return this.control.disabled;
    }

    @Input() control: FormControl;
    protected _options: any[] | null;
    @Input() set options(value: any[] | null) {
        this._options = value;
        this.filtered = this._filter(this.control.value);
    }
    get options() {
        return this._options;
    }


    readonly filterKey = input<string | string[]>('');
    readonly displayKey = input<string | string[]>('');
    readonly secondaryDisplayKey = input<string | string[]>();
    readonly accessor = input<(option: any) => string>();
    readonly valueKey = input<string>();
    readonly isLoading = input<boolean>(false);

    readonly local_filter = input<boolean>(true);

    readonly appearance = input<'fill' | 'outline'>('outline');
    readonly order = input<'asc' | 'desc' | 'none'>('none');
    @Input() local_pagination: {
        limit: number,
        skip: number,
    };
    readonly groupBy = input<string>();

    protected sub: Subscription = new Subscription();

    get field_disabled() {
        if (this.disabled !== undefined) {
            return this.disabled;
        } else {
            return this.control.disabled;
        }
    }

    get paginatedOptions() {
        if (this.local_pagination) {
            return this.filteredOptions.slice(this.local_pagination.skip, this.local_pagination.skip + this.local_pagination.limit);
        }
        else {
            return this.filteredOptions;
        }
    }

    get groupedOptions() {
        return _.map(_.groupBy(this.paginatedOptions, this.groupBy()), (value, key) => {
            return { slug: key, values: value };
        });
    }

    protected filtered: string[] | any[] = [];
    get filteredOptions(): any {
        return this.filtered; // this._filter(this.lastValue);
    };

    displayWith = (value: any) => {
        // console.log("ERROR", value, this.options, this.filteredOptions)
        if (value) {
            return typeof value === 'string' ? value : this._accessor(value, true);
        }
        else {
            // console.log("ERROR : Autocomplete no value [af]", value)
            return '';
        }
    };

    ngOnInit() {
        this.filtered = this.options || [];
        this.sub.add(this.control!.valueChanges.pipe(distinctUntilChanged())
            .subscribe((value) => {
                this.filtered = this._filter(value);
            }));

        // console.log('operateur_exe control is', this.form.control.operateur_exe.status);

        //     this.subscription.add(this.form.controls.operateur_exe.statusChanges.subscribe((status: any) => {
        //         if (status === 'DISABLED') {
        //             console.log('operateur_exe control is disabled');
        //         } else if (status === 'ENABLED') {
        //             console.log('operateur_exe control is enabled');
        //         }
        //     }));
    }

    filter() {
        if (this.local_pagination)
            this.local_pagination.skip = 0;
        if (this.control.value === '')
            this.control.setValue("");
    }

    protected _filter(value: string | any): string[] | any[] {
        let filterValue = '';
        if (typeof value === 'string')
            filterValue = value.toLowerCase();
        else
            filterValue = this._accessor(value)?.toLowerCase();
        if (this.options) {
            return this.options
                .filter(option => {
                    if(!this.local_filter()) return true; // disable local filtering
                    const optionValue = typeof option === 'string' ? option : this._accessor(option);
                    return optionValue !== undefined && !_.isEmpty(optionValue) && this._compare(filterValue, optionValue);
                })
                .sort((a, b) => {
                    const getComparableValue = (option: any) => {
                        const value = typeof option === 'string' ? option : this._accessor(option);
                        return value.toLowerCase();
                    };

                    const compareStrings = (str1: string, str2: string) => {
                        const order = this.order();
                        return order === 'asc' ? str1.localeCompare(str2) : (order === 'desc' ? str2.localeCompare(str1) : 0);
                    };

                    const aValue = getComparableValue(a);
                    const bValue = getComparableValue(b);

                    const aStartsWith = aValue.startsWith(filterValue);
                    const bStartsWith = bValue.startsWith(filterValue);
                    const aIncludes = aValue.includes(filterValue);
                    const bIncludes = bValue.includes(filterValue);

                    // 1. Si a commence par filterValue mais pas b, a doit être avant
                    if (aStartsWith && !bStartsWith) return -1;
                    // 2. Si b commence par filterValue mais pas a, b doit être avant
                    if (!aStartsWith && bStartsWith) return 1;
                    // 3. Si a et b commencent tous les deux par filterValue, les trier par ordre alphabétique
                    if (aStartsWith && bStartsWith) return compareStrings(aValue, bValue);
                    // 4. Si aucun des deux ne commence par filterValue mais ils le contiennent, les trier alphabétiquement
                    if (aIncludes && bIncludes) return compareStrings(aValue, bValue);
                    // 5. Si a contient filterValue mais pas b, a doit être avant
                    if (aIncludes && !bIncludes) return -1;
                    // 6. Si b contient filterValue mais pas a, b doit être avant
                    if (!aIncludes && bIncludes) return 1;
                    // 7. Sinon, trier par ordre alphabétique
                    return compareStrings(aValue, bValue);
                });
        }
        else return [];
    }

    protected _compare(filterValue: string, option: string) {
        // console.log(this.label, filterValue, !_.isEmpty(filterValue), option.toLowerCase().includes(_.trim(filterValue)));
        return (!_.isEmpty(filterValue)) ? option.toLowerCase().includes(_.trim(filterValue.toLowerCase())) : true;
    }

    getLabel(option: any) {
        return _.isString(option) ? option : this._accessor(option, true);
    }

    getSecondaryLabel(option: any) {
        const key = this.secondaryDisplayKey();
        if(!key) return '';
        let v = '';
        if (_.isArray(key)) {
            key.forEach(s => v = _.join([v, _.property(s)(option)], ' '));
        } else {
            v = _.property(key)(option) as string;
        }
        return v;
    }

    protected _accessor(option: any, display: boolean = false): string {
        const accessor = this.accessor();
        if (accessor) {
            return accessor(option);
        } else {
            const key = display ? this.displayKey() || this.filterKey() : this.filterKey();
            let v = '';
            if (_.isArray(key)) {
                key.forEach(s => v = _.join([v, _.property(s)(option)], ' '));
            } else {
                v = _.property(key)(option) as string;
            }
            return v;
        }
    }

    value(option: any) {
        // console.log("ERRORqsdfqsdfqs", option)
        const valueKey = this.valueKey();
        if (valueKey) {
            return _.get(option, valueKey);
        } else {
            return option;
        }
    }

    onPage(event: any) {
        if (this.local_pagination) {
            this.local_pagination.skip = event.pageIndex * event.pageSize;
        }
    }

    ngOnDestroy(): void {
        this.sub.unsubscribe();
    }

    trackByItems(index: number, item: any) {
        if (item._id) return item._id;
        if (item?.name) return item.name;
        if (item?.slug) return item.slug;
        if (item?.value) return item.value;
        return item;
    }
}
